import { Button, ButtonGroup, Icon, Modal } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import XRayGeneratorAcquisitionConstantsConfigurationByProfile from 'app/components/XRayGeneratorAcquisitionConstantsConfiguration/XRayGeneratorAcquisitionConstantsConfigurationByProfile';
import XRayGeneratorManagerContext from 'app/providers/XRayGeneratorManagerProvider/context';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CustomProvider } from 'rsuite';
import { AcquisitionConstantsProvider } from './AcquisitionConstantsContext';
import { selectAcquisitionConstants } from 'app/redux/xrayGeneratorConfiguration/reducer';
import { useDispatch, useSelector } from 'react-redux';
import {
  importAcquisitionConstantsProfiles,
  updateAcquisitionConstant,
} from 'app/redux/xrayGeneratorConfiguration/actions';
import ACQUISITION_CONSTANTS, { AcquisitionConstantsList } from 'app/utils/acquisitionConstants';
import _ from 'lodash';
import validateAcquisitionConstants from 'app/components/XRayGeneratorAcquisitionConstantsConfiguration/validateAcquisitionConstants';
import TechnicianPasswordValidationContext from 'app/providers/TechnicianPasswordValidationProvider/context';

export default function XRayGeneratorAcquisitionConstantsConfiguration() {
  const xRayGeneratorManager = useContext(XRayGeneratorManagerContext);
  const [modalOpen, setModalOpen] = React.useState(false);
  if (!xRayGeneratorManager) return null;
  const importRef = React.useRef<HTMLInputElement>(null);
  const requestValidation = useContext(TechnicianPasswordValidationContext);

  const openModal = () =>
    requestValidation()
      .then(() => setModalOpen(true))
      .catch(() => {});
  const closeModal = () => setModalOpen(false);
  const [importedAcquisitionConstants, setImportedAcquisitionConstants] = useState<
    Record<string, AcquisitionConstantsList> | undefined
  >(undefined);

  const dispatch = useDispatch();
  const reduxAcquisitionConstants = useSelector(selectAcquisitionConstants);

  const isConstantsValid = useMemo(() => {
    if (!importedAcquisitionConstants) return true;

    return Object.values(importedAcquisitionConstants).every((constantsForProfile) =>
      _.every(constantsForProfile, (data) => validateAcquisitionConstants(data) === undefined)
    );
  }, [importedAcquisitionConstants]);

  const onUpdateAcquisitionConstant = useCallback(
    (region: string, acquisitionConstants: AcquisitionConstantsList) => {
      if (importedAcquisitionConstants) {
        setImportedAcquisitionConstants((prev) => ({
          ..._.omitBy(prev, (value, key) => key.startsWith(region)),
          [region]: acquisitionConstants,
        }));
      } else {
        dispatch(updateAcquisitionConstant(region, acquisitionConstants));
      }
    },
    [dispatch, importedAcquisitionConstants]
  );

  const onAcquisitionConstantsImport = useCallback(() => {
    if (!importRef.current?.files || importRef.current.files.length === 0) {
      return;
    }

    const file = importRef.current.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        setImportedAcquisitionConstants(data);
      } catch (error) {
        console.error('Error parsing acquisition constants:', error);
        toast.error(<FormattedMessage id="general.invalid_file" />);
      }
    };

    reader.readAsText(file);
  }, []);

  const importAcquisitionConstants = () => {
    importRef.current?.click();
  };
  const confirmImportAcquisitionConstants = () => {
    if (!importedAcquisitionConstants) return;

    dispatch(importAcquisitionConstantsProfiles(importedAcquisitionConstants));
    toast.success(
      <FormattedMessage id="xray_generator.configuration.acquisition_constants.import_success" />
    );
    setImportedAcquisitionConstants(undefined);
  };

  const resetImportedAcquisitionConstants = () => {
    setImportedAcquisitionConstants(undefined);
  };
  const exportAcquisitionConstants = () => {
    const dataStr = JSON.stringify(reduxAcquisitionConstants);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'picoxia_acquisition_constants.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const acquisitionConstantsByProfile = importedAcquisitionConstants ?? reduxAcquisitionConstants;

  const isAcquisitionConstantsDefault = useMemo(() => {
    return _.isEqual(reduxAcquisitionConstants, ACQUISITION_CONSTANTS);
  }, [reduxAcquisitionConstants]);

  return (
    <CustomProvider theme="light">
      <ButtonGroup>
        <Modal
          trigger={
            <Button>
              <FormattedMessage id="general.configure" />
            </Button>
          }
          open={modalOpen}
          closeOnEscape={false}
          onOpen={openModal}
          onClose={closeModal}
          size="large"
          closeIcon
          closeOnDimmerClick={false}
          className="xray-generator-acquisition-constants-modal"
        >
          <Modal.Header>
            <FormattedMessage id="xray_generator.configuration.acquisition_constants.title" />
          </Modal.Header>
          <Modal.Content>
            <AcquisitionConstantsProvider
              acquisitionConstantsByProfile={acquisitionConstantsByProfile}
              onUpdateAcquisitionConstant={onUpdateAcquisitionConstant}
            >
              <XRayGeneratorAcquisitionConstantsConfigurationByProfile />
            </AcquisitionConstantsProvider>
          </Modal.Content>
          <Modal.Actions>
            <input
              type="file"
              data-testid="file-upload"
              accept=".json"
              onChange={onAcquisitionConstantsImport}
              ref={importRef}
              style={{ display: 'none' }}
            />
            {!importedAcquisitionConstants ? (
              <>
                <Button primary onClick={importAcquisitionConstants}>
                  <Icon name="upload" />
                  <FormattedMessage id="general.import" />
                </Button>
                <Button primary onClick={exportAcquisitionConstants}>
                  <Icon name="download" />
                  <FormattedMessage id="general.export" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  positive
                  onClick={confirmImportAcquisitionConstants}
                  disabled={!isConstantsValid}
                >
                  {!isConstantsValid && <Icon name="warning sign" />}
                  <FormattedMessage id="xray_generator.configuration.acquisition_constants.import_confirm" />
                </Button>
                <Button secondary onClick={resetImportedAcquisitionConstants}>
                  <FormattedMessage id="general.reset" />
                </Button>
              </>
            )}
            <Button secondary onClick={closeModal}>
              <FormattedMessage id="general.close" />
            </Button>
          </Modal.Actions>
        </Modal>
        {!isAcquisitionConstantsDefault && (
          <Button basic color="orange">
            <Icon name="warning sign" />
            <FormattedMessage id="xray_generator.configuration.acquisition_constants.non_default_warning" />
          </Button>
        )}
      </ButtonGroup>
    </CustomProvider>
  );
}
